/**
 * exportPdf
 *
 * Turns a single rendered DOM subtree — a chat message, an insight — into a
 * downloaded PDF file, via html2pdf.js (html2canvas + jsPDF).
 *
 * Key responsibilities:
 * - Clone the node into an offscreen, light-themed, fixed-width container so
 *   the captured image is a clean document rather than a screenshot of the app
 *   in dark mode at whatever width the phone happens to be
 * - Load html2pdf.js lazily, so ~500kB of canvas/PDF machinery stays out of
 *   the initial bundle and is fetched only when someone exports
 * - Produce a real .pdf download — one tap, no print dialog
 *
 * Trade-off, deliberately accepted: html2canvas rasterizes, so text in the
 * resulting PDF is an image — not selectable, not searchable, and larger than
 * a text PDF. The previous implementation drove the browser's print pipeline,
 * which preserved real text, but it routed through the OS print dialog and
 * could not be made to work on iOS Safari.
 *
 * Why a fixed render width rather than the on-screen width: the message bubble
 * is at most 80% of a phone viewport. Captured at that size the PDF is a
 * narrow column with enormous margins, and any table inside it is compressed
 * to the point of being unreadable.
 *
 * The clone is built in two layers, and the split is load-bearing. html2pdf
 * deep-clones whatever it is handed into a container of its own and rasterizes
 * *that container*. Any positioning on the element therefore travels with it:
 * an offscreen `position: fixed` node lands out of flow inside html2pdf's
 * container, contributes no height, and yields a correctly-sized blank page —
 * measured in Chromium as a 719x0 canvas. So the offscreen holder is an outer
 * wrapper that html2pdf never sees, and the node handed to it carries styling
 * but no positioning.
 */

/**
 * Width, in CSS pixels, that the clone is rendered at before capture.
 *
 * Roughly a portrait A4 content column at 96dpi. Wide enough that a
 * three-column table keeps readable columns.
 */
const RENDER_WIDTH_PX = 760;

const PDF_ROOT_ID = "attunewell-pdf-root";

/**
 * Styling for the clone.
 *
 * Every colour is stated explicitly: html2canvas captures *computed* styles, so
 * anything left to inherit picks up the app's theme tokens and a dark-mode
 * export comes out with white text on a dark background.
 */
const PDF_STYLES = `
  #${PDF_ROOT_ID} {
    /*
      Fills html2pdf's container, which it sizes to the PDF page width. A fixed
      px width here would either overflow the page or leave a margin down one
      side, depending on the paper size.
    */
    width: 100%;
    padding: 8px 4px;
    box-sizing: border-box;
    font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif;
    font-size: 15px;
    line-height: 1.6;
    color: #111827;
    background: #ffffff;
  }
  #${PDF_ROOT_ID} * {
    box-sizing: border-box;
    color: inherit;
    background: transparent;
    border-color: #d1d5db;
    max-width: 100%;
  }
  #${PDF_ROOT_ID} h1,
  #${PDF_ROOT_ID} h2,
  #${PDF_ROOT_ID} h3,
  #${PDF_ROOT_ID} h4 {
    line-height: 1.3;
    margin: 1.2em 0 0.5em;
    font-weight: 600;
  }
  #${PDF_ROOT_ID} h1 { font-size: 24px; }
  #${PDF_ROOT_ID} h2 { font-size: 20px; }
  #${PDF_ROOT_ID} h3 { font-size: 17px; }
  #${PDF_ROOT_ID} p { margin: 0 0 0.9em; }
  #${PDF_ROOT_ID} ul,
  #${PDF_ROOT_ID} ol { margin: 0 0 0.9em; padding-left: 1.4em; }
  #${PDF_ROOT_ID} li { margin: 0.25em 0; }
  #${PDF_ROOT_ID} blockquote {
    margin: 0 0 0.9em;
    padding-left: 0.9em;
    border-left: 3px solid #d1d5db;
    color: #4b5563;
  }
  #${PDF_ROOT_ID} code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.9em;
    background: #f3f4f6;
    padding: 0.1em 0.3em;
    border-radius: 3px;
  }
  #${PDF_ROOT_ID} pre {
    background: #f3f4f6;
    padding: 0.8em;
    border-radius: 4px;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
  #${PDF_ROOT_ID} pre code { background: none; padding: 0; }
  /*
    On screen the table lives in a horizontally scrolling wrapper with a
    min-width. Both have to go here: html2canvas captures only what is visible
    within a scroll container, so a scrolling wrapper would silently crop the
    right-hand columns out of the PDF.
  */
  #${PDF_ROOT_ID} div { overflow: visible !important; }
  #${PDF_ROOT_ID} table {
    width: 100% !important;
    min-width: 0 !important;
    table-layout: fixed;
    border-collapse: collapse;
    margin: 0 0 1em;
    font-size: 13px;
  }
  #${PDF_ROOT_ID} th,
  #${PDF_ROOT_ID} td {
    border: 1px solid #d1d5db;
    padding: 6px 9px;
    text-align: left;
    vertical-align: top;
    word-wrap: break-word;
    overflow-wrap: anywhere;
  }
  #${PDF_ROOT_ID} th { background: #f3f4f6; font-weight: 600; }
  #${PDF_ROOT_ID} a { color: #1d4ed8; text-decoration: underline; }
  #${PDF_ROOT_ID} img { max-width: 100%; }
  /* Screen-only affordances that have no meaning in a document. */
  #${PDF_ROOT_ID} button,
  #${PDF_ROOT_ID} [data-print-hide] { display: none !important; }
`;

/**
 * Make `title` safe to hand to a file system.
 *
 * Path separators and the Windows-reserved set are the ones that actually
 * break a download; everything else is left alone so the name stays readable.
 */
export function toPdfFilename(title: string): string {
  const cleaned = title
    // Whitespace first: newlines and tabs are themselves control characters,
    // so stripping controls before this turns a wrapped title into "a - b".
    .replace(/\s+/g, " ")
    // Path separators, the Windows-reserved set, and any remaining control
    // characters. eslint flags the literal control range, which is the point.
    // eslint-disable-next-line no-control-regex
    .replace(/[/\\?%*:|"<>\x00-\x1f]/g, "-")
    .trim()
    .slice(0, 80)
    // A trailing dot is invalid on Windows; a trailing dash or space just
    // reads as truncated.
    .replace(/[.\s-]+$/, "");

  return `${cleaned || "AttuneWell"}.pdf`;
}

/**
 * Build the offscreen holder and the node that is actually captured.
 *
 * `holder` is positioned far off to the left rather than hidden with
 * `display: none` or `visibility: hidden`: the cloner reads real layout via
 * `getBoundingClientRect`, and a node with no box measures as empty.
 *
 * `target` carries the styling and the content but **no positioning**, because
 * html2pdf clones it into its own container — see the note at the top of this
 * file for why positioning there produces a blank page.
 */
function createCaptureHost(element: HTMLElement): {
  holder: HTMLElement;
  target: HTMLElement;
} {
  const holder = document.createElement("div");
  holder.setAttribute("aria-hidden", "true");
  holder.style.cssText = [
    "position:fixed",
    "left:-10000px",
    "top:0",
    `width:${RENDER_WIDTH_PX}px`,
    "background:#ffffff",
    "pointer-events:none",
  ].join(";");

  const target = document.createElement("div");
  target.id = PDF_ROOT_ID;

  const style = document.createElement("style");
  style.textContent = PDF_STYLES;
  target.appendChild(style);

  const content = document.createElement("div");
  /*
    `element.innerHTML` is markup React already rendered from markdown.
    react-markdown does not pass raw HTML through without rehype-raw, which is
    not enabled here, so this is not a route for user-authored markup.
  */
  content.innerHTML = element.innerHTML;
  target.appendChild(content);

  holder.appendChild(target);
  return { holder, target };
}

/**
 * Export `element`'s rendered contents as a downloaded PDF.
 *
 * Resolves `true` when the file was produced, `false` when it was not — the
 * caller decides how to surface that. Never throws: a failed export should not
 * take down the message list.
 */
export async function exportElementToPdf(
  element: HTMLElement | null | undefined,
  title: string
): Promise<boolean> {
  if (!element || typeof document === "undefined") return false;

  const { holder, target } = createCaptureHost(element);
  document.body.appendChild(holder);

  try {
    // Lazy: keeps html2canvas + jsPDF out of the initial bundle.
    const { default: html2pdf } = await import("html2pdf.js");

    const options = {
      margin: [12, 10, 12, 10] as [number, number, number, number],
      filename: toPdfFilename(title),
      image: { type: "jpeg" as const, quality: 0.98 },
      enableLinks: false,
      html2canvas: {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      },
      jsPDF: {
        unit: "mm" as const,
        format: "a4" as const,
        orientation: "portrait" as const,
      },
      /*
        `pagebreak` is a real html2pdf.js option but is missing from the types
        the package ships, hence the cast below. Without it a table row can be
        sliced in half across a page boundary.
      */
      pagebreak: {
        mode: ["css", "legacy"],
        avoid: ["tr", "pre", "blockquote"],
      },
    };

    /*
      The worker is pulled out into its own binding so the cast can key off
      `typeof worker`. `ReturnType<typeof html2pdf>` resolves to the wrong
      overload — html2pdf is callable both as `html2pdf()` (worker) and as
      `html2pdf(element, options)` (Promise) — and picks up the Promise.
    */
    const worker = html2pdf();
    await worker
      .set(options as Parameters<typeof worker.set>[0])
      .from(target)
      .save();

    return true;
  } catch {
    // Out of memory on a very long message, a blocked download, a failed
    // chunk load for the lazy import — none of it is actionable by the user
    // beyond "try again", which the caller communicates.
    return false;
  } finally {
    holder.parentNode?.removeChild(holder);
  }
}
